/*
  Build Your Own System (BYOS) configurator — v2.

  v1 hardcoded 8 named packages in Liquid and looked each component up with its
  own all_products[handle] call. That hit Shopify's Liquid complexity budget
  (~22 lookups across 8 packages) and silently returned wrong product data for
  one package in production — invisible in code review, only caught by
  comparing live HTML against the stored theme source. The fix that stuck: a
  single collections.all.products paginate loop builds one flat "catalogue"
  array, and everything else resolves against it client-side.

  v2 goes further: there is no hardcoded package list at all. pickRecommended()
  below assembles ONE system on the fly from the catalogue, using each
  product's own role / speaker_type / room_size / price / title (for a brand
  guess) — see the section's Liquid comment for why this data shape is safe.
*/

// Package-logic rules finalized with the team lead (2026-10-01).
// An "or" swap or upsell may push a package up to 30% ABOVE the top of its
// tier's price range — upward only; the bottom of the range is never relaxed.
const BYOS_TIER_OVERSHOOT = 1.3;
// Height speakers sold as ONE matched pair (a single unit = both speakers).
// Every other height speaker is sold individually, so two are needed.
const BYOS_PAIR_SOLD_HEIGHT = ['osd-audio-ace600-in-ceiling-speaker'];
// Stands come with every bookshelf package except these, where they are a
// separate optional upsell (see the add-on on the tier).
const BYOS_STANDS_SEPARATE = ['wharfedale-elysian-2'];

if (!customElements.get('byo-system')) {
  customElements.define('byo-system', class ByoSystem extends HTMLElement {
    constructor() {
      super();
      const raw = this.querySelector('[data-byos-json]');
      this.data = raw ? JSON.parse(raw.textContent) : { catalogue: [], addons: [], musicTiers: [], musicExtras: [] };
      this.cartUrl = this.dataset.cartUrl;
      this.whatsappNumber = this.dataset.whatsappNumber;
      this.resolveMusicTiers();

      this.state = {
        category: null, // music | tv | homecinema
        systemType: null, // "2.0" | "2.1" | "3.1" | "5.1" | "5.1.2"
        wireType: null, // "wireless" | "wired" — Music (2.0/2.1) only
        room: null,
        matters: null,
        budget: null,
        frontStyle: null, // "bookshelf" | "floorstanding" — theatre (3.1/5.1/5.1.2) only
        recommended: null, // { components: [...], addon, brand, why }
        addonSelected: false, // whether the optional AV Receiver add-on is switched on
        expandedRow: null // which Customize row is expanded: a components[] index, "addon", or null
      };
      this.history = [];
      this.current = 'hero';

      this.pricebar = this.querySelector('[data-byos-pricebar]');

      this.bindCategoryTiles();
      this.bindConfigSteps();
      this.bindCustomizeNext();
      this.bindCheckout();
      this.bindBackButtons();
      this.bindHelpModal();
      this.bindHelpTooltips();
      this.renderProgress();
    }

    // "Help me choose" tooltips on each question section's header. Hover/focus
    // shows them via CSS alone; this only adds a tap-to-toggle path for touch
    // devices, which have no hover state.
    bindHelpTooltips() {
      const triggers = this.querySelectorAll('[data-byos-help-tip]');
      triggers.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const wasOpen = btn.classList.contains('is-open');
          triggers.forEach((t) => t.classList.remove('is-open'));
          if (!wasOpen) btn.classList.add('is-open');
        });
      });
      document.addEventListener('click', () => {
        triggers.forEach((t) => t.classList.remove('is-open'));
      });
    }

    // ---------- helpers ----------

    formatMoney(cents) {
      const amount = Math.round((cents || 0) / 100);
      return 'Rs ' + amount.toLocaleString('en-PK');
    }

    // Free, local placeholder for the handful of products with no real photo
    // in Shopify yet (e.g. Integra DRX 5.4) — a plain inline SVG, no external
    // request, no license/attribution needed. Swap for real product photos
    // later; this is only here so the layout doesn't show a blank box.
    placeholderImage() {
      const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">' +
        '<rect width="120" height="120" fill="#f8f6fa"/>' +
        '<rect x="38" y="20" width="44" height="80" rx="6" fill="none" stroke="#c9b8d6" stroke-width="3"/>' +
        '<circle cx="60" cy="38" r="9" fill="none" stroke="#c9b8d6" stroke-width="3"/>' +
        '<circle cx="60" cy="72" r="14" fill="none" stroke="#c9b8d6" stroke-width="3"/>' +
        '</svg>';
      return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
    }

    imageFor(component) {
      return (component && component.image) || this.placeholderImage();
    }

    brandOf(product) {
      // No brand field exists in the catalogue — every product title starts
      // with its brand ("JBL Stage A130...", "Wharfedale Diamond 12.1...",
      // "WiiM Amp Pro..."), so the first word is a reliable, zero-maintenance
      // brand signal that needs no hardcoded product list.
      return (product.title || '').split(' ')[0];
    }

    // Music runs on Uzair's own curated tier ladder (musicTiers), not the
    // dynamic assembler used for TV/Home Cinema. Tier components only carry
    // a handle — fill in real price/title/image from "catalogue" plus the 3
    // extras (2 untagged bundle SKUs + the Quad amp, tagged "All-in-One" and
    // so outside the catalogue filter) that aren't in catalogue.
    resolveMusicTiers() {
      const byHandle = {};
      (this.data.catalogue || []).forEach((p) => { byHandle[p.handle] = p; });
      (this.data.musicExtras || []).forEach((p) => { byHandle[p.handle] = p; });
      this.resolveTierArray(this.data.musicTiers, byHandle, 'music');

      // TV (3.1/5.1/5.1.2) and Home Cinema (5.1/5.1.2) tier ladder — nearly
      // every product here is component_role-tagged and resolves straight
      // against "catalogue", plus theatreExtras (the DX-3 5.1 bundle SKU,
      // same untagged-bundle exception as musicExtras above).
      const theatreByHandle = {};
      (this.data.catalogue || []).forEach((p) => { theatreByHandle[p.handle] = p; });
      (this.data.theatreExtras || []).forEach((p) => { theatreByHandle[p.handle] = p; });
      this.resolveTierArray(this.data.theatreTiers, theatreByHandle, 'theatre');
    }

    resolveTierArray(tiers, byHandle, label) {
      (tiers || []).forEach((tier) => {
        tier.components = tier.components.map((c) => {
          const full = byHandle[c.handle];
          if (!full) {
            console.error('BYOS: ' + label + ' tier "' + tier.key + '" references unknown handle "' + c.handle + '"');
            return c;
          }
          return Object.assign({
            handle: full.handle, id: full.id, variant_id: full.variant_id, title: full.title,
            image: full.image, price: full.price, speaker_type: full.speaker_type,
            rooms: full.rooms, watts: full.watts,
            qty: c.qty || 1
          }, { slot: c.slot, label: c.label });
        });
        // Optional add-on (e.g. the AV Receiver on theatre tiers) — resolved
        // the same way as a component, but kept OUT of tier.total: it's not
        // part of the recommended package's headline price, only added in
        // if the visitor switches it on in Customize. See resolveMusicTiers().
        if (tier.addon) {
          const full = byHandle[tier.addon.handle];
          if (!full) {
            console.error('BYOS: ' + label + ' tier "' + tier.key + '" references unknown addon handle "' + tier.addon.handle + '"');
            tier.addon = null;
          } else {
            tier.addon = Object.assign({
              handle: full.handle, id: full.id, variant_id: full.variant_id, title: full.title,
              image: full.image, price: full.price, speaker_type: full.speaker_type,
              rooms: full.rooms, watts: full.watts,
              qty: tier.addon.qty || 1
            }, { slot: tier.addon.slot, label: tier.addon.label, note: tier.addon.note });
          }
        }
        // Theatre-only: the two front-speaker variants (Bookshelf/Floorstanding)
        // a visitor can choose between for this tier — resolved the same way
        // as a normal component, but kept OUT of tier.total (only the tier's
        // default front, already in components[], counts toward it). See
        // pickTheatreTier(), which swaps components[] to match the answer.
        if (tier.frontOptions) {
          const resolvedFrontOptions = {};
          ['bookshelf', 'floorstanding'].forEach((style) => {
            const handle = tier.frontOptions[style];
            const full = byHandle[handle];
            if (!full) {
              console.error('BYOS: ' + label + ' tier "' + tier.key + '" references unknown frontOptions.' + style + ' handle "' + handle + '"');
              return;
            }
            resolvedFrontOptions[style] = {
              handle: full.handle, id: full.id, variant_id: full.variant_id, title: full.title,
              image: full.image, price: full.price, speaker_type: full.speaker_type,
              rooms: full.rooms, watts: full.watts, qty: 1, slot: 'front'
            };
          });
          tier.frontOptions = resolvedFrontOptions;
        }
        tier.total = tier.components.reduce((sum, c) => sum + c.price * c.qty, 0);
        tier.brand = this.brandOf(tier.components[0]);
      });
    }

    goTo(step, opts) {
      opts = opts || {};
      if (!opts.isBack) this.history.push(this.current);
      this.current = step;
      this.querySelectorAll('.byos-step').forEach((el) => {
        el.classList.toggle('is-active', el.dataset.byosStep === step);
      });
      // final-review already shows its own total inline (.byos-checkout__total),
      // so the shared pricebar only needs to appear on recommended/customize —
      // and directly above that step's action row, not pinned at the page top.
      const pricebarSteps = { customize: '[data-byos-step="customize"] [data-customize-end]' };
      const targetSelector = pricebarSteps[step];
      this.pricebar.hidden = !targetSelector;
      if (targetSelector) {
        const target = this.querySelector(targetSelector);
        if (target && target.parentNode) target.parentNode.insertBefore(this.pricebar, target);
      }
      this.renderProgress();
      window.scrollTo({ top: this.offsetTop - 20, behavior: 'smooth' });
    }

    // ---------- shared progress bar (Music + TV; Home Cinema too, since it's
    // one shared component — only its styling/copy is out of scope this pass) ----------

    // The wizard has no single numeric "step" counter: music/tv/homecinema
    // -config screens ask room + preferences + budget on one screen, and
    // Music's Wireless path skips all three. So each of the 5 named stops is
    // derived from this.current + this.state instead of tracked separately —
    // it can never drift out of sync with what's actually on screen.
    computeProgressStatuses() {
      if (this.current === 'consult') return null;

      const statuses = ['upcoming', 'upcoming', 'upcoming', 'upcoming', 'upcoming'];

      if (this.current === 'hero') {
        statuses[0] = 'active';
        return statuses;
      }
      statuses[0] = 'complete';

      const skipsRest = this.state.category === 'music' && this.state.wireType === 'wireless';
      const spaceDone = skipsRest || !!this.state.room;
      const preferencesDone = skipsRest || !!this.state.matters;
      const budgetDone = skipsRest || !!this.state.budget;

      if (this.current.endsWith('-config')) {
        statuses[1] = spaceDone ? 'complete' : 'active';
        statuses[2] = !spaceDone ? 'upcoming' : (preferencesDone ? 'complete' : 'active');
        statuses[3] = !preferencesDone ? 'upcoming' : (budgetDone ? 'complete' : 'active');
        statuses[4] = 'upcoming';
        return statuses;
      }

      // customize / final-review: Category through Budget are behind us, Results is current.
      statuses[1] = 'complete';
      statuses[2] = 'complete';
      statuses[3] = 'complete';
      statuses[4] = 'active';
      return statuses;
    }

    renderProgress() {
      const wrap = this.querySelector('.byos-step.is-active [data-byos-progress]');
      if (!wrap) return;
      const statuses = this.computeProgressStatuses();
      if (!statuses) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      wrap.querySelectorAll('[data-progress-stop]').forEach((item, i) => {
        item.classList.remove('is-complete', 'is-active', 'is-upcoming');
        item.classList.add('is-' + statuses[i]);
        const circle = item.querySelector('.byos-progress__circle');
        circle.innerHTML = statuses[i] === 'complete'
          ? '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : String(i + 1);
      });
      let activeIndex = statuses.indexOf('active');
      if (activeIndex === -1) activeIndex = statuses.lastIndexOf('complete');
      if (activeIndex === -1) activeIndex = 0;
      const label = wrap.querySelector('[data-byos-progress-label]');
      if (label) label.textContent = 'Step ' + (activeIndex + 1) + ' of 5';
    }

    bindBackButtons() {
      this.querySelectorAll('[data-byos-back]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const prev = this.history.pop();
          if (prev) this.goTo(prev, { isBack: true });
        });
      });
    }

    // One shared "Need help?" modal, opened from a trigger button on every
    // step's topbar. Sends whatever the visitor types straight to WhatsApp —
    // no lead is captured server-side, this is just a faster path to a human
    // than digging up the phone number.
    bindHelpModal() {
      const modal = this.querySelector('[data-help-modal]');
      if (!modal) return;
      const textarea = this.querySelector('[data-help-query]');

      this.querySelectorAll('[data-byos-help-trigger]').forEach((btn) => {
        btn.addEventListener('click', () => {
          textarea.value = '';
          modal.hidden = false;
          textarea.focus();
        });
      });

      const close = () => { modal.hidden = true; };
      this.querySelector('[data-help-modal-close]').addEventListener('click', close);
      modal.addEventListener('click', (e) => { if (e.target === modal) close(); });

      this.querySelector('[data-help-modal-submit]').addEventListener('click', () => {
        const query = textarea.value.trim();
        if (!query) { textarea.focus(); return; }
        const message = "Hi Cinematics, I'm using the Build Your Own System tool and had a question:\n\n" + query;
        window.open('https://wa.me/' + this.whatsappNumber + '?text=' + encodeURIComponent(message), '_blank');
        close();
      });
    }

    // serviceSlug routes the "Get in touch" link straight into the matching
    // service on the rebuilt /pages/contact form (skips to Step 2, pre-
    // selected) instead of a static message — turns these dead ends into
    // real captured leads.
    showConsult(message, serviceSlug) {
      this.querySelector('[data-consult-message]').textContent = message;
      const link = this.querySelector('[data-consult-link]');
      if (link) link.href = serviceSlug ? '/pages/contact?service=' + serviceSlug : '/pages/contact';
      this.goTo('consult');
    }

    // ---------- step: hero / category ----------

    bindCategoryTiles() {
      this.querySelectorAll('[data-category-tiles] [data-category]').forEach((tile) => {
        tile.addEventListener('click', () => {
          const category = tile.dataset.category;
          if (category === 'wholehome') {
            this.showConsult('Whole-home audio setups are custom-designed around your rooms and how you use them. Get in touch and our team will call you to plan it out.', 'whole-home');
            return;
          }
          if (category === 'commercial') {
            this.showConsult('Commercial spaces need a site-specific design. Get in touch and our team will scope it with you.', 'commercial');
            return;
          }
          this.state.category = category;
          this.resetConfig();
          this.goTo(category + '-config');
        });
      });
    }

    resetConfig() {
      this.state.systemType = null;
      this.state.wireType = null;
      this.state.room = null;
      this.state.matters = null;
      this.state.budget = null;
      this.state.frontStyle = null;
      // Keep the screens in step with the cleared answers — otherwise a
      // second visit to a category shows tiles still highlighted for
      // answers the engine no longer holds.
      this.querySelectorAll('[data-byos-step$="-config"]').forEach((stepEl) => {
        stepEl.querySelectorAll('[data-value].is-selected, [data-value].is-recommended').forEach((t) => t.classList.remove('is-selected', 'is-recommended'));
        stepEl.querySelectorAll('[data-recommend-hint]').forEach((h) => { h.hidden = true; });
        stepEl.querySelectorAll('[data-homecinema-rest], [data-music-wired-rest], [data-theatre-front-question]').forEach((el) => { el.hidden = true; });
        this.updateBudgetFloor(stepEl);
      });
    }

    // ---------- step: per-category config screens ----------

    bindConfigSteps() {
      this.querySelectorAll('[data-byos-step$="-config"]').forEach((stepEl) => {
        // Home Cinema's room/matters/budget block only appears once 5.1 or
        // 5.1.2 is picked ("Custom Built" routes to consult instead).
        stepEl.querySelectorAll('[data-field="systemType"] [data-value]').forEach((tile) => {
          tile.addEventListener('click', () => {
            const value = tile.dataset.value;
            if (stepEl.dataset.byosStep === 'homecinema-config' && value === 'custom') {
              this.showConsult('A fully custom home cinema is designed around your room, seating and screen — not a quiz. Get in touch and our team will design it with you.', 'home-cinema');
              return;
            }
            this.state.systemType = value;
            this.highlightGroup(tile.closest('[data-field]'), tile);
            this.updateBudgetFloor(stepEl);
            const rest = stepEl.querySelector('[data-homecinema-rest]');
            if (rest) rest.hidden = false;
            // TV config mixes Music (2.0/2.1) and theatre (3.1/5.1/5.1.2)
            // system types on one screen — the front-speaker-style question
            // only applies to theatre tiers, so it's hidden/shown per pick
            // rather than always visible like Home Cinema's (always theatre).
            const frontQ = stepEl.querySelector('[data-theatre-front-question]');
            if (frontQ) {
              const isTheatre = ['3.1', '5.1', '5.1.2'].includes(value);
              frontQ.hidden = !isTheatre;
              if (!isTheatre) {
                this.state.frontStyle = null;
                frontQ.querySelectorAll('[data-value]').forEach((t) => t.classList.remove('is-selected'));
              }
              this.updateBudgetFloor(stepEl);
            }
          });
        });

        // Music-only: Wireless vs Wired, asked before room/matters/budget.
        // Wireless is a single fixed WiiM package per channel count, so
        // picking it skips the rest of the quiz entirely; Wired reveals it.
        stepEl.querySelectorAll('[data-field="wireType"] [data-value]').forEach((tile) => {
          tile.addEventListener('click', () => {
            const value = tile.dataset.value;
            this.state.wireType = value;
            this.highlightGroup(tile.closest('[data-field]'), tile);
            const rest = stepEl.querySelector('[data-music-wired-rest]');
            if (rest) {
              rest.hidden = value !== 'wired';
              if (value !== 'wired') {
                this.state.room = null;
                this.state.matters = null;
                this.state.budget = null;
                rest.querySelectorAll('[data-value]').forEach((t) => t.classList.remove('is-selected'));
              }
            }
            this.renderProgress();
          });
        });

        stepEl.querySelectorAll('[data-field="room"] [data-value], [data-field="matters"] [data-value], [data-field="budget"] [data-value], [data-field="frontStyle"] [data-value]').forEach((tile) => {
          tile.addEventListener('click', () => {
            const field = tile.closest('[data-field]').dataset.field;
            this.state[field] = tile.dataset.value;
            this.highlightGroup(tile.closest('[data-field]'), tile);
            this.updateSystemHint(stepEl);
            this.updateBudgetFloor(stepEl);
            this.renderProgress();
          });
        });

        const nextBtn = stepEl.querySelector('[data-config-next]');
        if (nextBtn) {
          nextBtn.addEventListener('click', () => {
            const frontQ = stepEl.querySelector('[data-theatre-front-question]');
            const needsFrontStyle = frontQ && !frontQ.hidden;
            const wireField = stepEl.querySelector('[data-field="wireType"]');
            const isWireless = !!wireField && this.state.wireType === 'wireless';
            const needsRoomQuiz = !isWireless;
            if (!this.state.systemType || (!!wireField && !this.state.wireType)
              || (needsRoomQuiz && (!this.state.room || !this.state.matters || !this.state.budget))
              || (needsFrontStyle && !this.state.frontStyle)) {
              nextBtn.textContent = 'Please answer every question above';
              setTimeout(() => { nextBtn.textContent = 'Next →'; }, 1800);
              return;
            }
            this.runRecommendation();
          });
        }
      });
    }

    highlightGroup(groupEl, activeTile) {
      groupEl.querySelectorAll('[data-value]').forEach((t) => t.classList.toggle('is-selected', t === activeTile));
    }

    // As soon as room + preference are known (before the system-type choice
    // is required), tell the visitor which option we'd actually recommend —
    // this is advisory only, it never overrides what they click themselves.
    suggestedSystemTypes(matters, available) {
      // "What matters most" narrows a POOL of system sizes for the visitor
      // to choose from — it never forces one answer, and overlap between
      // answers (dialogue and movies both surfacing 5.1/5.1.2) is expected.
      const pools = {
        dialogue: ['3.1', '5.1', '5.1.2'],
        bass: ['2.1', '3.1', '5.1', '5.1.2'], // everything except 2.0
        movies: ['5.1', '5.1.2'],
        music: ['2.0', '2.1'],
        detail: ['2.0', '2.1'],
        soundstage: ['2.0', '2.1'],
        minimal: ['2.0', '2.1']
      };
      const wanted = pools[matters] || [];
      const hit = available.filter((t) => wanted.includes(t));
      // A screen may not offer any size in the pool (Home Cinema has no
      // 2.x) — then point at the smallest size it does offer.
      return hit.length ? hit : available.slice(0, 1);
    }

    updateSystemHint(stepEl) {
      const hint = stepEl.querySelector('[data-recommend-hint]');
      if (!hint) return;
      const tilesGroup = stepEl.querySelector('[data-field="systemType"]');
      const tiles = Array.from(tilesGroup.querySelectorAll('[data-value]'));
      const available = tiles.map((t) => t.dataset.value).filter((v) => /^\d/.test(v));
      const suggestions = this.state.matters ? this.suggestedSystemTypes(this.state.matters, available) : [];
      tiles.forEach((t) => {
        t.classList.toggle('is-recommended', suggestions.includes(t.dataset.value));
      });
      if (!suggestions.length) {
        hint.hidden = true;
        return;
      }
      const mattersLabel = {
        music: 'music quality', bass: 'powerful bass', detail: 'detail', soundstage: 'a wide soundstage',
        minimal: 'a minimal setup', dialogue: 'clear dialogue', movies: 'immersive movies'
      }[this.state.matters] || this.state.matters;
      const list = suggestions.length > 1
        ? suggestions.slice(0, -1).join(', ') + ' or ' + suggestions[suggestions.length - 1]
        : suggestions[0];
      hint.textContent = `For ${mattersLabel}, we'd suggest ${list}. Pick whichever suits your room and budget.`;
      hint.hidden = false;
    }

    // Floor-standing speakers (any room size): the cheapest bookshelf
    // package is no longer a fair "starting price", so the lowest budget
    // range(s) below the cheapest available floor-standing package are
    // greyed out and the real starting price is shown instead.
    cheapestFloorstanding(systemType) {
      if (['3.1', '5.1', '5.1.2'].includes(systemType)) {
        const totals = (this.data.theatreTiers || [])
          .filter((t) => t.channels === systemType && !(t.fixedFront && t.fixedFront !== 'floorstanding'))
          .map((t) => this.applyFrontStyle(t, 'floorstanding').total);
        return totals.length ? Math.min(...totals) : null;
      }
      return null;
    }

    updateBudgetFloor(stepEl) {
      const group = stepEl.querySelector('[data-field="budget"]');
      if (!group) return;
      const active = this.state.frontStyle === 'floorstanding';
      const floor = active ? this.cheapestFloorstanding(this.state.systemType) : null;
      group.querySelectorAll('[data-value]').forEach((tile) => {
        let upperRs = Number(tile.dataset.value);
        if (!upperRs || upperRs >= 900000000) upperRs = Infinity;
        const below = floor !== null && upperRs * 100 < floor;
        tile.disabled = below;
        tile.classList.toggle('is-below-floor', below);
        if (below && this.state.budget === tile.dataset.value) {
          this.state.budget = null;
          tile.classList.remove('is-selected');
        }
      });
      let note = stepEl.querySelector('[data-budget-floor-note]');
      if (floor === null) {
        if (note) note.hidden = true;
        return;
      }
      if (!note) {
        note = document.createElement('p');
        note.className = 'byos-recommend-hint byos-floor-note';
        note.setAttribute('data-budget-floor-note', '');
        group.insertAdjacentElement('afterend', note);
      }
      note.textContent = `Floor-standing systems for this setup start from ${this.formatMoney(floor)}, so the lower budget ranges aren't available with that choice.`;
      note.hidden = false;
    }

    // ---------- recommendation engine ----------

    // How many units of a product make a full height-speaker pair.
    heightQty(handle) {
      return BYOS_PAIR_SOLD_HEIGHT.includes(handle) ? 1 : 2;
    }

    slotsForSystemType(systemType) {
      const map = {
        '2.0': ['front', 'amp'],
        '2.1': ['front', 'amp', 'sub'],
        '3.1': ['front', 'center', 'sub', 'receiver'],
        '5.1': ['front', 'center', 'surround', 'sub', 'receiver'],
        '5.1.2': ['front', 'center', 'surround', 'sub', 'receiver', 'height']
      };
      return map[systemType] || ['front', 'amp'];
    }

    frontSpeakerType(room, matters) {
      if (matters === 'minimal') return 'Bookshelf';
      if (room === 'small room') return 'Bookshelf';
      if (room === 'large room') return 'Floorstanding';
      // medium room
      return ['detail', 'soundstage', 'movies'].includes(matters) ? 'Floorstanding' : 'Bookshelf';
    }

    poolFor(slot, ctx) {
      const cat = this.data.catalogue.filter((p) => p.available !== false);
      const roomOk = (p) => (p.rooms || []).includes(ctx.room);
      switch (slot) {
        case 'front': {
          const type = ctx.frontType;
          let pool = cat.filter((p) => p.role === 'Speaker' && (p.speaker_type || []).includes(type));
          const inRoom = pool.filter(roomOk);
          return inRoom.length ? inRoom : pool;
        }
        case 'center':
          // Center channels are never tagged for small rooms, and a center
          // is still required in a small-room 3.1 setup — so this slot is
          // deliberately never room-filtered.
          return cat.filter((p) => p.role === 'Speaker' && (p.speaker_type || []).includes('Center Channel Speaker'));
        case 'surround': {
          let pool = cat.filter((p) => p.role === 'Speaker' && (p.speaker_type || []).includes('Surround'));
          const inRoom = pool.filter(roomOk);
          return inRoom.length ? inRoom : pool;
        }
        case 'sub': {
          let pool = cat.filter((p) => p.role === 'Subwoofer');
          const inRoom = pool.filter(roomOk);
          return inRoom.length ? inRoom : pool;
        }
        case 'amp':
          return cat.filter((p) => p.role === 'Amplifier');
        case 'receiver':
          return cat.filter((p) => p.role === 'AV Receiver');
        case 'height':
          return cat.filter((p) => (p.tags || []).includes('atmos-height') || (p.speaker_type || []).includes('Height Speaker'));
        default:
          return [];
      }
    }

    // Pick one item from a pool, biased by the "what matters most" answer.
    pickFromPool(pool, matters, slot) {
      if (!pool.length) return null;
      const sorted = pool.slice().sort((a, b) => a.price - b.price);
      if (matters === 'minimal') return sorted[0];
      if (matters === 'bass' && slot === 'sub') {
        return pool.slice().sort((a, b) => (b.watts || 0) - (a.watts || 0))[0];
      }
      if (['detail', 'soundstage'].includes(matters) && slot === 'front') {
        return sorted[sorted.length - 1];
      }
      if (matters === 'dialogue' && slot === 'center') {
        return sorted[Math.floor(sorted.length / 2)];
      }
      // default: a balanced mid-tier pick, not the cheapest and not the priciest
      return sorted[Math.floor(sorted.length / 2)];
    }

    // Build one full system for a given "anchor" brand (the front speaker's
    // brand) — same-brand parts where the brand makes them, cross-brand
    // fallback (by price/preference) for slots that brand doesn't cover, e.g.
    // no speaker brand here makes AV receivers.
    assembleForBrand(brand, slots, ctx) {
      const components = [];
      let total = 0;
      for (const slot of slots) {
        const pool = this.poolFor(slot, ctx);
        if (!pool.length) return null; // catalogue can't fill this system at all
        const sameBrand = pool.filter((p) => this.brandOf(p) === brand);
        const picked = this.pickFromPool(sameBrand.length ? sameBrand : pool, ctx.matters, slot);
        if (!picked) return null;
        components.push(Object.assign({ slot: slot, qty: slot === 'height' ? this.heightQty(picked.handle) : 1 }, picked));
        total += picked.price * (slot === 'height' ? this.heightQty(picked.handle) : 1);
      }
      return { brand: brand, components: components, total: total };
    }

    // Budget tiles pass their upper bound only ("500000" for "Under 500K"),
    // but recommending needs the specific band, not just "under some ceiling"
    // — otherwise every tier from "Under 500K" up to "2M+" keeps returning
    // whatever the single priciest in-catalogue system happens to be, which
    // is exactly what looked like "ignoring budget" in practice.
    budgetRange(budgetValue) {
      // Two real bugs fixed here:
      // 1) Budget tile values are rupees ("500000"); every product/tier
      //    "price"/"total" in this file is in money subunits (cents) — see
      //    formatMoney(). Comparing the two unconverted meant every band
      //    matched almost nothing, so the "closest fit" fallback silently
      //    won every time regardless of what was picked — the real cause
      //    of "budget doesn't do anything."
      // 2) The "2M+" tile's literal value (999999999, used so it's still a
      //    real number in a <input data-value>) never matched the Infinity
      //    bound below, so that band always fell back to "0 to ~999,999,999"
      //    instead of "2,000,000 and up".
      const bounds = [0, 500000, 1000000, 2000000, Infinity];
      let upperRs = Number(budgetValue);
      if (!upperRs || upperRs >= 900000000) upperRs = Infinity;
      const idx = bounds.indexOf(upperRs);
      const lowerRs = idx > 0 ? bounds[idx - 1] : 0;
      return [lowerRs * 100, upperRs === Infinity ? Infinity : upperRs * 100];
    }

    // Uzair's own curated tier ladder for Music — filter to the picked
    // channel count, band-match by budget the same way the dynamic engine
    // does, then use "matters" to lean toward the cheaper or pricier end of
    // whatever's in-band. Where two real options exist at the same rung
    // (JBL vs Wharfedale Basic, Audiolab vs Quad High-End), default to
    // Wharfedale/Audiolab for brand continuity with the rest of the ladder —
    // the other one is still reachable as a swap on the Customize screen.
    // Shared by pickMusicTier and pickTheatreTier: band-filter a set of
    // tiers by budget, falling back to whichever tier(s) sit closest to the
    // band when nothing actually fits inside it, sorted cheapest-first.
    tiersInBand(tiers, ctx) {
      const [lower, upper] = this.budgetRange(ctx.budget);
      const distance = (t) => (t.total < lower ? lower - t.total : t.total - upper);
      let pool = tiers.filter((t) => t.total >= lower && t.total <= upper);
      if (!pool.length) {
        const bestDist = Math.min(...tiers.map(distance));
        pool = tiers.filter((t) => distance(t) === bestDist);
      }
      return pool.slice().sort((a, b) => a.total - b.total);
    }

    // Whichever slot carries the front speakers/system for a music tier
    // ("front" for normal tiers, "system" for the bundle/wireless ones).
    musicTierFront(tier) {
      return tier.components.find((c) => c.slot === 'front' || c.slot === 'system');
    }

    // Task #5: within the Wired ladder, present cheaper AND bookshelf
    // options ahead of pricier/floorstanding ones — Bookshelf speakers
    // group first (cheapest to priciest), then any Floorstanding tiers
    // (also cheapest to priciest). A stable sort keeps this grouping intact
    // when tiersInBand() re-sorts by price alone afterwards.
    sortMusicTiersBookshelfFirst(tiers) {
      const isBookshelf = (t) => (this.musicTierFront(t).speaker_type || [])[0] === 'Bookshelf';
      return tiers.slice().sort((a, b) => {
        const ra = isBookshelf(a) ? 0 : 1;
        const rb = isBookshelf(b) ? 0 : 1;
        if (ra !== rb) return ra - rb;
        return a.total - b.total;
      });
    }

    // The price range a package's swaps and upsells are measured against:
    // lowest to highest total across every package in the same tier of the
    // same system size (both front-speaker styles counted for theatre, both
    // "or" options for Music). Customize may go up to 30% above the top —
    // never below the bottom.
    tierBand(tier, isMusic) {
      const siblings = (isMusic ? this.data.musicTiers : this.data.theatreTiers || [])
        .filter((t) => t.channels === tier.channels && (isMusic ? t.tier_label === tier.tier_label : t.tier === tier.tier));
      const totals = [];
      siblings.forEach((t) => {
        if (!isMusic && t.frontOptions) {
          ['bookshelf', 'floorstanding'].forEach((style) => totals.push(this.applyFrontStyle(t, style).total));
        } else {
          totals.push(t.total);
        }
      });
      return { min: Math.min(...totals), max: Math.max(...totals) };
    }

    // Swap a theatre tier's default front speaker for the chosen style.
    // Tiers without a separate front slot (the DX-3 bundle) come back
    // untouched, same as Music's indivisible bundle/wireless tiers.
    applyFrontStyle(tier, style) {
      const clone = JSON.parse(JSON.stringify(tier));
      if (!style || !clone.frontOptions || !clone.frontOptions[style]) return clone;
      const frontIndex = clone.components.findIndex((c) => c.slot === 'front');
      if (frontIndex === -1) return clone;
      clone.components[frontIndex] = Object.assign({}, clone.frontOptions[style], {
        label: clone.components[frontIndex].label,
        qty: clone.components[frontIndex].qty || 1
      });
      clone.total = clone.components.reduce((sum, c) => sum + c.price * c.qty, 0);
      clone.brand = this.brandOf(clone.components[0]);
      return clone;
    }

    // "Or" tiebreak (Basic: JBL or Wharfedale; High-End: Audiolab or Quad):
    // show the pricier of the two when it fits the price range of the
    // visitor's own budget band (top of that range plus the 30% overshoot),
    // otherwise the cheaper. There is no separate budget figure to compare
    // against — the band's own spread decides it.
    resolveOrOption(chosen, pool, tiers) {
      const rivals = tiers.filter((t) => t.key !== chosen.key && t.channels === chosen.channels && t.tier_label === chosen.tier_label);
      if (!rivals.length) return chosen;
      const rangeTop = Math.max(...pool.map((t) => t.total));
      const options = [chosen].concat(rivals).sort((a, b) => b.total - a.total);
      return options.find((t) => t.total <= rangeTop * BYOS_TIER_OVERSHOOT) || options[options.length - 1];
    }

    pickMusicTier(ctx) {
      const allTiers = (this.data.musicTiers || []).filter((t) => t.channels === ctx.systemType);
      if (!allTiers.length) return null;

      // Wireless is its own upfront choice — a single fixed WiiM package
      // per channel count, so it skips the room/matters/budget quiz.
      if (ctx.wireType === 'wireless') {
        const wireless = allTiers.find((t) => t.tier === 'wireless');
        if (!wireless) return null;
        const clone = JSON.parse(JSON.stringify(wireless));
        clone.why = 'You chose WiiM Wireless — streaming speakers with no separate amplifier or speaker cables, just plug in and play.';
        clone.band = this.tierBand(wireless, true);
        return clone;
      }

      // Wired: the Wireless tier is asked separately above, and the rest
      // are ordered cheapest-bookshelf-first before budget/matters narrow
      // them down.
      const tiers = this.sortMusicTiersBookshelfFirst(allTiers.filter((t) => t.tier !== 'wireless'));
      const pool = this.tiersInBand(tiers, ctx);

      let chosen;
      if (ctx.matters === 'minimal') {
        chosen = pool[0];
      } else if (['detail', 'soundstage', 'music'].includes(ctx.matters)) {
        chosen = pool[pool.length - 1];
      } else {
        chosen = pool[Math.floor((pool.length - 1) / 2)];
      }
      chosen = this.resolveOrOption(chosen, pool, tiers);

      const clone = JSON.parse(JSON.stringify(chosen));
      clone.band = this.tierBand(chosen, true);
      clone.why = this.explain(ctx, clone);
      return clone;
    }

    // TV (3.1/5.1/5.1.2) and Home Cinema (5.1/5.1.2) — same band-match +
    // matters-bias pattern as Music, over the theatreTiers ladder. The
    // visitor's Bookshelf/Floorstanding answer is applied to EVERY tier
    // first, so budget matching sees each package's real price in that
    // style (a floor-standing front can move a tier up a band) instead of
    // the tier's default-front price.
    pickTheatreTier(ctx) {
      const tiers = (this.data.theatreTiers || []).filter((t) => t.channels === ctx.systemType);
      if (!tiers.length) return null;
      let eligible = tiers.filter((t) => !(t.fixedFront && ctx.frontStyle && t.fixedFront !== ctx.frontStyle));
      if (!eligible.length) eligible = tiers;
      const variants = eligible.map((t) => this.applyFrontStyle(t, ctx.frontStyle));
      const pool = this.tiersInBand(variants, ctx);

      let chosen;
      if (ctx.matters === 'minimal') {
        chosen = pool[0];
      } else if (['detail', 'soundstage', 'music', 'movies', 'dialogue', 'bass'].includes(ctx.matters)) {
        chosen = pool[pool.length - 1];
      } else {
        chosen = pool[Math.floor((pool.length - 1) / 2)];
      }

      const clone = JSON.parse(JSON.stringify(chosen));
      clone.band = this.tierBand(chosen, false);
      clone.why = this.explain(ctx, clone);
      return clone;
    }

    pickRecommended(ctx) {
      if (ctx.systemType === '2.0' || ctx.systemType === '2.1') return this.pickMusicTier(ctx);
      if (['3.1', '5.1', '5.1.2'].includes(ctx.systemType)) return this.pickTheatreTier(ctx);

      const slots = this.slotsForSystemType(ctx.systemType);
      const frontPool = this.poolFor('front', ctx);
      if (!frontPool.length) return null;

      // Brand candidates come from BOTH the front-speaker pool and the
      // amp/receiver pool — a speaker brand alone would silently exclude
      // amp-only brands like WiiM, which never make their own speakers but
      // are a real, popular anchor (e.g. "JBL x WiiM", "Wharfedale x WiiM").
      const brandSet = new Set(frontPool.map((p) => this.brandOf(p)));
      const primarySlot = slots.includes('amp') ? 'amp' : (slots.includes('receiver') ? 'receiver' : null);
      if (primarySlot) this.poolFor(primarySlot, ctx).forEach((p) => brandSet.add(this.brandOf(p)));

      const candidates = Array.from(brandSet)
        .map((brand) => this.assembleForBrand(brand, slots, ctx))
        .filter(Boolean);
      if (!candidates.length) return null;

      const [lower, upper] = this.budgetRange(ctx.budget);
      const inBand = candidates.filter((c) => c.total >= lower && c.total <= upper);
      let pool;
      if (inBand.length) {
        pool = inBand;
      } else {
        // Nothing sits in the exact band the visitor picked — get as close
        // to it as possible instead of silently jumping to a different tier.
        pool = candidates.slice().sort((a, b) => {
          const da = a.total < lower ? lower - a.total : a.total - upper;
          const db = b.total < lower ? lower - b.total : b.total - upper;
          return da - db;
        });
        pool = [pool[0]];
      }
      // Among candidates that actually fit the chosen band, pick a balanced
      // one (closest to the middle of what's available) rather than always
      // maxing out to the priciest — "detail"/"soundstage" still lean higher,
      // "minimal" still leans cheaper, via the per-slot bias already applied.
      pool.sort((a, b) => a.total - b.total);
      const chosen = pool[Math.floor((pool.length - 1) / 2)];

      chosen.why = this.explain(ctx, chosen);
      return chosen;
    }

    explain(ctx, chosen) {
      const roomLabel = (ctx.room || '').replace(' room', '');
      const mattersLabel = {
        music: 'music quality', bass: 'powerful bass', detail: 'detail', soundstage: 'a wide soundstage',
        minimal: 'a minimal setup', dialogue: 'clear dialogue', movies: 'immersive movies'
      }[ctx.matters] || ctx.matters;
      const front = chosen.components.find((c) => c.slot === 'front' || c.slot === 'system');
      const frontType = (front.speaker_type || [])[0]; // undefined for bundle/wireless tiers with no real speaker_type
      const speakerPhrase = frontType ? `${frontType.toLowerCase()} speakers` : 'speakers';
      return `Based on your ${roomLabel} room and your focus on ${mattersLabel}, we built this around ${chosen.brand} ${speakerPhrase} for the front stage, sized to fit your space and budget.`;
    }

    runRecommendation() {
      const ctx = {
        category: this.state.category,
        room: this.state.room,
        matters: this.state.matters,
        budget: this.state.budget,
        systemType: this.state.systemType,
        wireType: this.state.wireType,
        frontType: this.frontSpeakerType(this.state.room, this.state.matters),
        frontStyle: this.state.frontStyle
      };
      const result = this.pickRecommended(ctx);
      if (!result) {
        this.showConsult("We don't have enough in-stock products to assemble this exact combination yet. Get in touch and we'll put something together for you.");
        return;
      }
      this.state.recommended = result;
      // Snapshot of what the quiz actually recommended, kept untouched
      // through any later swaps on Customize, so "what was recommended" is
      // always reachable again even after other slots (or a Music
      // whole-tier swap) change the running total out from under it.
      this.state.originalRecommended = JSON.parse(JSON.stringify(result));
      this.state.addonSelected = false;
      this.renderCustomize();
      this.goTo('customize');
    }

    slotLabel(slot, component) {
      if (component && component.label) return component.label;
      const map = {
        front: (component.speaker_type || [])[0] === 'Floorstanding' ? 'Tower Speakers (Front)' : 'Bookshelf Speakers (Front)',
        center: 'Center Channel',
        surround: 'Surround Speakers',
        sub: 'Subwoofer',
        amp: 'Amplifier',
        receiver: 'AV Receiver',
        height: 'Atmos Height Speakers',
        stands: 'Speaker Stands'
      };
      return map[slot] || slot;
    }

    // ---------- step: customize — recommended package summary ----------

    // Shown pinned at the top of Customize (task #7: the old standalone
    // "Your Recommended System" screen is gone — the quiz lands straight on
    // Customize, with the recommendation surfaced here instead of its own
    // page). Informational only: badge/name/why, no price — the running
    // total in the price bar below already covers that, and duplicating it
    // here would just invite the two numbers to disagree once a swap is made.
    renderRecommendedSummary() {
      const rec = this.state.recommended;
      const wrap = this.querySelector('[data-recommended-summary]');
      if (!wrap || !rec) return;
      // The "face" of the package for the thumbnail: whichever component is
      // the front speakers (or the whole bundle, for Music's indivisible
      // tiers) — same lookup musicTierFront() uses, just not Music-only.
      const face = rec.components.find((c) => c.slot === 'front' || c.slot === 'system') || rec.components[0];
      const total = this.computeSystemTotal();
      const badges = this.recommendationBadges(rec, total);
      wrap.innerHTML = `
        <span class="byos-recommended-tile__badge">Recommended for you</span>
        <div class="byos-recommended-tile__intro">
          <img class="byos-package__thumb" src="${this.imageFor(face)}" alt="${rec.brand} ${this.state.systemType} System">
          <div class="byos-package__summary-text">
            <h3 class="byos-package__name">${rec.brand} ${this.state.systemType} System</h3>
            <p class="byos-recommended__why">${rec.why}</p>
          </div>
          <div class="byos-recommended-tile__total">
            <span class="byos-recommended-tile__total-label">Estimated Total</span>
            <span class="byos-recommended-tile__total-value" data-recommended-total>${this.formatMoney(total)}</span>
            <button type="button" class="btn byos-recommended-tile__cta" data-use-this-system>Use this system &rarr;</button>
          </div>
        </div>
        ${this.roomNote(rec) ? `<p class="byos-room-note">${this.roomNote(rec)}</p>` : ''}
        ${badges.length ? `<div class="byos-recommended-tile__badges">${badges.map((b) => `
          <span class="byos-badge byos-badge--${b.kind}">${b.icon}<span>${b.text}</span></span>
        `).join('')}</div>` : ''}
      `;
      const cta = wrap.querySelector('[data-use-this-system]');
      if (cta) cta.addEventListener('click', () => this.useRecommendedSystem());
    }

    // Soft, non-blocking note when the front speakers don't suit the room
    // size — both directions. The visitor can always carry on regardless.
    roomNote(rec) {
      const front = rec.components.find((c) => c.slot === 'front');
      if (!front) return '';
      const type = (front.speaker_type || [])[0];
      if (type === 'Bookshelf' && this.state.room === 'large room') {
        return 'Good to know: bookshelf speakers can feel light in a large room. You can carry on with this pick, or tap Change on the front speakers to see floor-standing options.';
      }
      if (type === 'Floorstanding' && this.state.room === 'small room') {
        return 'Good to know: floor-standing speakers can overpower a small room. You can carry on with this pick, or tap Change on the front speakers to see bookshelf options.';
      }
      return '';
    }

    // Package total WITHOUT the optional add-on (receiver / stands) — what
    // the tier price range is compared against.
    packageTotal() {
      const rec = this.state.recommended;
      return rec ? rec.components.reduce((sum, c) => sum + c.price * (c.qty || 1), 0) : 0;
    }

    // Range a Customize swap must keep the whole package inside: the
    // selected tier's price range, topped up by the 30% overshoot.
    swapRange() {
      const orig = this.state.originalRecommended;
      if (orig && orig.band) return [orig.band.min, orig.band.max * BYOS_TIER_OVERSHOOT];
      return this.budgetRange(this.state.budget);
    }

    // Same total math as updateRunningTotal(), pulled out so the recommended
    // tile's own "Estimated Total" can read it before that function's first
    // run in renderCustomize() (and after, so the two never drift apart).
    computeSystemTotal() {
      const rec = this.state.recommended;
      if (!rec) return 0;
      let total = rec.components.reduce((sum, c) => sum + c.price * c.qty, 0);
      if (this.state.addonSelected && rec.addon) total += rec.addon.price * (rec.addon.qty || 1);
      return total;
    }

    // Small inline icon set for the recommendation badges — no icon font in
    // this theme, and pulling one in for three glyphs isn't worth it.
    badgeIcon(kind) {
      const icons = {
        star: '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6-4.5-4.2 6.1-.7z"/></svg>',
        bass: '<svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="10" width="3" height="5" rx="1.5"/><rect x="7.5" y="6" width="3" height="13" rx="1.5"/><rect x="13" y="1.5" width="3" height="21" rx="1.5"/><rect x="18.5" y="7" width="3" height="11" rx="1.5"/></svg>',
        note: '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
        minimal: '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none"/></svg>',
        check: '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12.4l2.6 2.6L16 9.5"/></svg>'
      };
      return icons[kind] || '';
    }

    // The 3 reason-tags under the recommended tile (mockup: "Great for your
    // room size" / "Deep, impactful bass" / "Best value in this range").
    // Reuses computeScore()'s room-fit/bass numbers so this can't disagree
    // with the score bars shown later on Review, and derives "value" from
    // where this system's total actually sits in the chosen budget band
    // rather than just always claiming it.
    recommendationBadges(rec, total) {
      const score = this.computeScore();
      const badges = [];
      if (score.roomFit >= 8) {
        badges.push({ kind: 'room', icon: this.badgeIcon('star'), text: 'Great for your room size' });
      }
      // "What matters most" covers more answers on the TV/Home Cinema quiz
      // (dialogue, movies, detail, soundstage) than just bass/minimal — each
      // gets its own reason-tag so a movie- or dialogue-focused build isn't
      // left with only the room badge while a bass-focused one gets two.
      const mattersBadge = {
        bass: { kind: 'bass', icon: 'bass', text: 'Deep, impactful bass' },
        minimal: { kind: 'minimal', icon: 'minimal', text: 'Minimal, easy setup' },
        movies: { kind: 'music', icon: 'note', text: 'Immersive movie sound' },
        dialogue: { kind: 'music', icon: 'note', text: 'Clear, crisp dialogue' },
        detail: { kind: 'music', icon: 'note', text: 'Detailed, accurate sound' },
        soundstage: { kind: 'music', icon: 'note', text: 'Wide, spacious soundstage' },
        music: { kind: 'music', icon: 'note', text: 'Rich, detailed sound' }
      }[this.state.matters];
      if (mattersBadge) {
        badges.push({ kind: mattersBadge.kind, icon: this.badgeIcon(mattersBadge.icon), text: mattersBadge.text });
      } else if (score.bass >= 7) {
        badges.push({ kind: 'bass', icon: this.badgeIcon('bass'), text: 'Deep, impactful bass' });
      } else if (score.music >= 8) {
        badges.push({ kind: 'music', icon: this.badgeIcon('note'), text: 'Rich, detailed sound' });
      }
      const [lower, upper] = this.budgetRange(this.state.budget);
      const span = (upper === Infinity ? lower * 2 || total || 1 : upper - lower) || 1;
      const position = (total - lower) / span;
      if (upper === Infinity || position <= 0.65) {
        badges.push({ kind: 'value', icon: this.badgeIcon('check'), text: 'Best value in this range' });
      }
      return badges.slice(0, 3);
    }

    // Shortcut CTA on the recommended tile itself: discard any swaps made on
    // this visit and go straight to Review with exactly what the quiz
    // recommended — for the visitor who doesn't want to customize at all.
    useRecommendedSystem() {
      const orig = this.state.originalRecommended;
      if (orig) this.state.recommended = JSON.parse(JSON.stringify(orig));
      this.state.addonSelected = false;
      this.renderFinalReview();
      this.goTo('final-review');
    }

    updateRunningTotal() {
      const rec = this.state.recommended;
      if (!rec) return;
      const total = this.computeSystemTotal();
      rec.total = total;
      const formatted = this.formatMoney(total);
      this.querySelector('[data-byos-running-total]').textContent = formatted;
      const checkoutTotal = this.querySelector('[data-checkout-total]');
      if (checkoutTotal) checkoutTotal.textContent = formatted;
      const recommendedTotal = this.querySelector('[data-recommended-total]');
      if (recommendedTotal) recommendedTotal.textContent = formatted;
      this.renderPricebarItems(rec);
    }

    // Thumbnails + item count in the sticky "Your System" bar — one thumb
    // per component row (the addon too, once switched on) so it always
    // mirrors exactly what renderComponentList shows above it.
    renderPricebarItems(rec) {
      const thumbs = this.querySelector('[data-byos-pricebar-thumbs]');
      const count = this.querySelector('[data-byos-pricebar-count]');
      if (!thumbs || !count) return;
      const items = rec.components.slice();
      if (this.state.addonSelected && rec.addon) items.push(rec.addon);
      count.textContent = `(${items.length} item${items.length === 1 ? '' : 's'})`;
      thumbs.innerHTML = items.map((item) => `
        <span class="byos-pricebar__item">
          <img src="${this.imageFor(item)}" alt="${item.tierName || item.title}">
          <span class="byos-pricebar__item-text">
            <span class="byos-pricebar__item-title">${item.tierName || item.title}</span>
            <span class="byos-pricebar__item-price">${this.formatMoney(item.price * (item.qty || 1))}</span>
          </span>
        </span>
      `).join('');
    }

    // ---------- step: customize (Current / Upgrade / Premium) ----------

    alternatesFor(component, slot) {
      // Music is a curated tier ladder, not independently swappable parts —
      // an amp/sub mismatched from a different tier would break the
      // brand-coherent system Uzair actually designed. So the front/system
      // slot offers whole OTHER TIERS as the "alternate" (swapping the
      // entire recommended system, priced at that tier's real total),
      // rather than individual products. Amp/sub slots still swap normally
      // within the current tier, same as TV/Home Cinema.
      if (this.state.category === 'music' && (slot === 'front' || slot === 'system')) {
        const currentKey = this.state.recommended.key;
        // Wireless and Wired are separate upfront choices now (task #5) —
        // Customize only offers alternates within whichever one the
        // visitor already picked, ordered cheapest-bookshelf-first.
        const isWireless = this.state.wireType === 'wireless';
        let tiers = (this.data.musicTiers || [])
          .filter((t) => t.channels === this.state.systemType && t.key !== currentKey)
          .filter((t) => (t.tier === 'wireless') === isWireless);
        tiers = this.sortMusicTiersBookshelfFirst(tiers);
        return tiers.map((t) => {
          const rep = this.musicTierFront(t);
          return Object.assign({}, rep, { price: t.total, tierKey: t.key, tierName: t.name });
        });
      }
      const ctx = { room: this.state.room, matters: this.state.matters, frontType: (component.speaker_type || [])[0] };
      const pool = this.poolFor(slot, ctx).filter((p) => p.handle !== component.handle);
      const alternates = slot === 'height'
        ? pool.map((p) => Object.assign({}, p, { qty: this.heightQty(p.handle) }))
        : pool;
      return alternates.slice().sort((a, b) => (a.price * (a.qty || 1)) - (b.price * (b.qty || 1)));
    }

    // v4 (2026-09-26): the per-slot tab bar is gone — every component
    // (Tower Speakers, Center Channel, Surround, Subwoofer, Atmos Height,
    // and the optional AV Receiver) is its own row in one stacked list,
    // always visible, each with its own price. Hitting "Customize" on a row
    // expands a horizontal-scroll strip of alternatives right under that
    // row (not a whole-page swap); picking one collapses it back to the
    // updated row. Real product handles get a "View details" link straight
    // to that product's page. Per Sakina's direction: no more "next
    // category" step — everything customizable is visible at once.
    renderCustomize() {
      this.renderRecommendedSummary();
      this.state.expandedRow = null;
      this.renderComponentList();
      this.updateRunningTotal();
    }

    renderComponentList() {
      const rec = this.state.recommended;
      const wrap = this.querySelector('[data-customize-slot]');
      const rows = rec.components.map((c, i) => this.componentRowHtml(c, i, this.state.expandedRow === i)).join('');
      const addonRow = rec.addon ? this.addonRowHtml() : '';
      const expandedIndex = this.state.expandedRow;
      const panel = (expandedIndex !== null && rec.components[expandedIndex])
        ? this.pickerPanelHtml(rec.components[expandedIndex], expandedIndex)
        : '';
      // The addon card AND the expanded picker panel both render OUTSIDE the
      // grid (their own full-width blocks), not as a grid-column:1/-1 item
      // inside it — a spanning item sharing the same auto-fit grid as the
      // individually-placed cards made the grid's column-count calculation
      // blow up to fit the picker's un-wrapped horizontal content, squeezing
      // every OTHER row into a handful of near-zero-width columns (the
      // "one letter per line" bug). Keeping the panel as a plain sibling
      // avoids that failure mode entirely, regardless of how wide its
      // contents are.
      wrap.innerHTML = `<div class="byos-component-grid">${rows}</div>${panel}${addonRow}`;
      this.bindComponentList();
    }

    // Stands come with every bookshelf package except Elysian 2.
    standsNote(component) {
      if (!['front', 'surround'].includes(component.slot)) return '';
      if (!(component.speaker_type || []).includes('Bookshelf')) return '';
      return BYOS_STANDS_SEPARATE.includes(component.handle)
        ? '<span class="byos-component-row__note">Stands sold separately &mdash; see the optional add-on below.</span>'
        : '<span class="byos-component-row__note">Stands included</span>';
    }

    componentRowHtml(component, index, expanded) {
      const label = this.slotLabel(component.slot, component);
      const displayTitle = component.tierName || component.title;
      const detailsLink = component.handle
        ? `<a href="/products/${component.handle}" target="_blank" rel="noopener" class="byos-component-row__details">View product details &#8599;</a>`
        : '';
      return `
        <div class="byos-component-row${expanded ? ' is-expanded' : ''}">
          <div class="byos-component-row__main">
            <img class="byos-component-row__photo" src="${this.imageFor(component)}" alt="${displayTitle}">
            <div class="byos-component-row__info">
              <span class="byos-component-row__label">${label}</span>
              <span class="byos-component-row__title">${displayTitle}</span>
              <span class="byos-component-row__price">${this.formatMoney(component.price * (component.qty || 1))}</span>
              ${detailsLink}
              ${this.standsNote(component)}
            </div>
            <button type="button" class="btn btn--secondary byos-component-row__change" data-toggle-row="${index}">${expanded ? 'Close' : 'Change'}</button>
          </div>
        </div>
      `;
    }

    // Full-width panel for whichever row is currently expanded — a sibling
    // of the grid (see renderComponentList), not nested inside the row.
    // Wraps pickerHtmlFor's cards with a header naming the slot being
    // swapped and a close button, plus prev/next nav arrows either side of
    // the scroll strip instead of a bare native scrollbar.
    pickerPanelHtml(current, index) {
      const label = this.slotLabel(current.slot, current);
      const num = String(index + 1).padStart(2, '0');
      return `
        <div class="byos-component-picker-panel">
          <div class="byos-component-picker-panel__header">
            <span class="byos-component-picker-panel__title"><span class="byos-component-picker-panel__num">${num}</span> &mdash; ${label}</span>
          </div>
          <div class="byos-component-picker-viewport">
            ${this.pickerHtmlFor(current, index)}
          </div>
        </div>
      `;
    }

    // Optional add-on (the AV Receiver on theatre tiers, the speaker stands on Elysian 2 tiers) —
    // one fixed product per tier, no alternatives to swap between, so it
    // only gets the on/off toggle, not a Customize picker. Still rendered
    // as a row in the same list for one consistent visual language.
    addonRowHtml() {
      const rec = this.state.recommended;
      const addon = rec.addon;
      const on = this.state.addonSelected;
      const detailsLink = addon.handle
        ? `<a href="/products/${addon.handle}" target="_blank" rel="noopener" class="byos-component-row__details">View product details &#8599;</a>`
        : '';
      return `
        <div class="byos-component-row byos-component-row--addon${on ? ' is-active' : ''}">
          <div class="byos-component-row__main">
            <img class="byos-component-row__photo" src="${this.imageFor(addon)}" alt="${addon.title}">
            <div class="byos-component-row__info">
              <span class="byos-component-row__label">Optional add-on · ${this.slotLabel(addon.slot, addon)}</span>
              <span class="byos-component-row__title">${addon.title}</span>
              <span class="byos-component-row__price">${on ? '+' : ''}${this.formatMoney(addon.price * (addon.qty || 1))}</span>
              ${detailsLink}
              <span class="byos-component-row__note">${addon.note || 'Already have a receiver, or building around a soundbar/powered setup? Leave this off.'}</span>
            </div>
            <button type="button" class="btn btn--secondary" data-toggle-addon>${on ? 'Remove' : 'Add to System'}</button>
          </div>
        </div>
      `;
    }

    // The horizontal-scroll strip of alternatives for one row, expanded in
    // place under it. Same budget-band filtering and "keep the original
    // recommendation reachable" logic as before — just rendered as a scroll
    // strip instead of a full-width grid.
    pickerHtmlFor(current, index) {
      const rec = this.state.recommended;
      const alternates = this.alternatesFor(current, current.slot);
      // Whole-tier alternates (Music front/system slot) are priced by the
      // WHOLE system total, so deltas — and the budget-band check below —
      // must compare against the current system's total, not this one
      // component's price.
      const isTierSwap = alternates.length > 0 && alternates[0].tierKey;

      // Show every real alternative that keeps the OVERALL system inside
      // the selected tier's price range (plus the 30% upward overshoot) (not the individual
      // component's own price) — swapping one component changes the whole
      // system's total, so that's what has to stay in-band.
      const [lower, upper] = this.swapRange();
      const resultingTotal = (a) => (isTierSwap ? a.price : this.packageTotal() - (current.price * (current.qty || 1)) + (a.price * (a.qty || 1)));
      const inBudget = alternates
        .filter((a) => { const t = resultingTotal(a); return t >= lower && t <= upper; })
        .sort((a, b) => a.price - b.price);

      // Whatever the quiz originally recommended for this slot (or, for a
      // Music whole-tier swap, the whole original tier) stays reachable
      // even after it's fallen outside the current budget-band filter —
      // e.g. because a DIFFERENT slot got upgraded, or the AV Receiver
      // add-on got toggled on, pushing the total around. Otherwise
      // swapping something else away and then trying to come back to what
      // was recommended has no way back. Never filtered by budget.
      const orig = this.state.originalRecommended;
      let originalCard = null;
      if (isTierSwap) {
        if (orig && orig.key && orig.key !== rec.key) {
          const tier = (this.data.musicTiers || []).find((t) => t.key === orig.key);
          if (tier) {
            const rep = this.musicTierFront(tier);
            originalCard = Object.assign({}, rep, { price: tier.total, tierKey: tier.key, tierName: tier.name });
          }
        }
      } else if (orig && orig.components && orig.components[index] && orig.components[index].handle !== current.handle) {
        originalCard = orig.components[index];
      }

      const cards = [{ product: current }];
      if (originalCard) cards.push({ product: originalCard });
      const originalKey = originalCard && (originalCard.tierKey || originalCard.handle);
      inBudget.forEach((product) => {
        const key = product.tierKey || product.handle;
        if (key === originalKey) return; // already added above, don't show it twice
        cards.push({ product });
      });

      return `
        <div class="byos-component-picker" data-picker-track>
          ${cards.map((card) => {
            const displayTitle = card.product.tierName || card.product.title;
            const cardKey = card.product.tierKey || card.product.handle;
            const isCurrent = cardKey === (current.tierKey || current.handle);
            const displayPrice = isTierSwap ? card.product.price : card.product.price * (card.product.qty || 1);
            return `
            <article class="byos-tier-card${isCurrent ? ' is-selected' : ''}" data-select-tier="${card.product.handle}" data-tier-key="${card.product.tierKey || ''}">
              <img src="${this.imageFor(card.product)}" alt="${displayTitle}">
              <div class="byos-tier-card__body">
                <span class="byos-tier-card__title">${displayTitle}</span>
                <span class="byos-tier-card__price">${this.formatMoney(displayPrice)}</span>
                ${card.product.handle ? `<a href="/products/${card.product.handle}" target="_blank" rel="noopener" class="byos-tier-card__details">View product details &#8599;</a>` : ''}
              </div>
            </article>
          `;
          }).join('')}
        </div>
      `;
    }

    bindComponentList() {
      const wrap = this.querySelector('[data-customize-slot]');

      wrap.querySelectorAll('[data-toggle-row]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = Number(btn.dataset.toggleRow);
          this.state.expandedRow = this.state.expandedRow === idx ? null : idx;
          this.renderComponentList();
        });
      });

      const addonBtn = wrap.querySelector('[data-toggle-addon]');
      if (addonBtn) {
        addonBtn.addEventListener('click', () => {
          this.state.addonSelected = !this.state.addonSelected;
          this.updateRunningTotal();
          // Adding/removing the receiver can move the total in or out of
          // the "best value" band, so the badges need a refresh too, not
          // just the total figure.
          this.renderRecommendedSummary();
          this.renderComponentList();
        });
      }

      this.bindPickerNav(wrap);

      wrap.querySelectorAll('[data-select-tier]').forEach((card) => {
        if (card.classList.contains('is-selected')) return;
        card.addEventListener('click', (e) => {
          if (e.target.closest('a')) return; // "View product details" navigates instead of selecting
          const rec = this.state.recommended;
          const index = this.state.expandedRow;
          const current = rec.components[index];
          const tierKey = card.dataset.tierKey;
          if (tierKey) {
            const tier = (this.data.musicTiers || []).find((t) => t.key === tierKey);
            if (!tier) return;
            this.state.recommended = JSON.parse(JSON.stringify(tier));
            this.state.recommended.why = this.explain(
              { room: this.state.room, matters: this.state.matters },
              this.state.recommended
            );
            this.state.expandedRow = null;
            this.state.addonSelected = false; // the new tier may carry a different (or no) add-on
            this.updateRunningTotal();
            this.renderCustomize();
            return;
          }
          const handle = card.dataset.selectTier;
          const replacement = this.data.catalogue.find((p) => p.handle === handle) || current;
          rec.components[index] = Object.assign({ slot: current.slot, qty: current.qty }, replacement);
          if (current.slot === 'height') rec.components[index].qty = this.heightQty(replacement.handle);
          this.state.expandedRow = null;
          this.updateRunningTotal();
          this.renderRecommendedSummary();
          this.renderComponentList();
        });
      });
    }

    // Prev/next arrows for the expanded alternatives strip, replacing the
    // bare horizontal scrollbar. Scrolls by roughly two cards at a time and
    // disables whichever arrow has nothing further to scroll to.
    bindPickerNav(wrap) {
      const track = wrap.querySelector('[data-picker-track]');
      const prevBtn = wrap.querySelector('[data-picker-prev]');
      const nextBtn = wrap.querySelector('[data-picker-next]');
      if (!track || !prevBtn || !nextBtn) return;

      const step = () => {
        const card = track.querySelector('.byos-tier-card');
        if (!card) return 240;
        // Some older browsers report flexbox gap as "normal" rather than a
        // pixel value, which parseFloat turns into NaN — falling back to 14
        // (the CSS gap) keeps the arrows working there too.
        const gap = parseFloat(window.getComputedStyle(track).columnGap) || 14;
        return (card.getBoundingClientRect().width + gap) * 2;
      };
      const updateNavState = () => {
        const maxScroll = track.scrollWidth - track.clientWidth - 1;
        prevBtn.disabled = track.scrollLeft <= 0;
        nextBtn.disabled = maxScroll <= 0 || track.scrollLeft >= maxScroll;
      };

      prevBtn.addEventListener('click', () => track.scrollBy({ left: -step() }));
      nextBtn.addEventListener('click', () => track.scrollBy({ left: step() }));
      track.addEventListener('scroll', updateNavState);
      updateNavState();
    }

    bindCustomizeNext() {
      const btn = this.querySelector('[data-customize-next]');
      if (!btn) return;
      btn.addEventListener('click', () => {
        this.renderFinalReview();
        this.goTo('final-review');
      });
    }

    // ---------- step: final review + system score ----------

    computeScore() {
      const rec = this.state.recommended;
      const systemType = this.state.systemType;
      const hasSub = rec.components.some((c) => c.slot === 'sub');
      const hasHeight = rec.components.some((c) => c.slot === 'height');
      const baseChannels = { '2.0': 2, '2.1': 2, '3.1': 3, '5.1': 5, '5.1.2': 5 }[systemType] || 2;
      const front = rec.components.find((c) => c.slot === 'front' || c.slot === 'system');
      const sub = rec.components.find((c) => c.slot === 'sub');
      const amp = rec.components.find((c) => c.slot === 'amp' || c.slot === 'receiver') || (this.state.addonSelected ? rec.addon : null);

      const clamp = (n) => Math.max(0, Math.min(10, Math.round(n)));
      const movies = clamp(3 + baseChannels * 0.8 + (hasSub ? 1.5 : 0) + (hasHeight ? 2 : 0));
      const music = clamp(5 + ((front.speaker_type || [])[0] === 'Floorstanding' ? 1.5 : 0) + ((amp && amp.watts >= 100) ? 1 : 0) + (['music', 'detail', 'soundstage'].includes(this.state.matters) ? 1 : 0));
      const bass = clamp(hasSub ? 4 + Math.min(4, (sub.watts || 100) / 60) : 3);
      const roomFit = clamp(this.estimateRoomFit(rec.components) * 10);
      // Movies is a TV/Home Cinema-only score — a pure Music build has no
      // movie-viewing use case to score, so it's left out of that average.
      const isMusic = this.state.category === 'music';
      const overall = clamp(isMusic ? (music + bass + roomFit) / 3 : (movies + music + bass + roomFit) / 4);

      return { movies, music, bass, roomFit, overall };
    }

    // Room Fit used to be a strict "does this exact component list this
    // exact room" ratio — honest, but it cliffs straight to 0/10 the
    // moment even one untagged or size-mismatched part is in the mix
    // (a receiver only tagged for "large room" in an otherwise-fine
    // small-room system, say), which reads as "broken" rather than "a bit
    // oversized". Estimated instead: each room-tagged component gets full,
    // partial, or low credit based on how far its tagged size(s) are from
    // the visitor's pick (adjacent sizes still mostly work; opposite ends
    // barely do), the front/system speaker counts double since it's the
    // component the room size is really chosen around, and anything with
    // no room tag at all is skipped rather than counted as a miss. No
    // room answered at all (the Wireless quick-pick skips that question)
    // → no signal either way, so a neutral baseline instead of 0.
    estimateRoomFit(components) {
      if (!this.state.room) return 0.7;
      const rank = { 'small room': 0, 'medium room': 1, 'large room': 2 };
      const target = rank[this.state.room];
      if (target === undefined) return 0.7;
      const creditFor = (distance) => (distance === 0 ? 1 : distance === 1 ? 0.6 : 0.2);
      let weighted = 0;
      let weightTotal = 0;
      components.forEach((c) => {
        const tags = (c.rooms || []).map((r) => rank[r]).filter((r) => r !== undefined);
        if (!tags.length) return; // no room tag on this part — not counted either way
        const distance = Math.min(...tags.map((r) => Math.abs(r - target)));
        const weight = (c.slot === 'front' || c.slot === 'system') ? 2 : 1;
        weighted += creditFor(distance) * weight;
        weightTotal += weight;
      });
      return weightTotal ? weighted / weightTotal : 0.7; // nothing tagged at all — same neutral baseline
    }

    // Small icon set for the Room/Use/Configuration summary pills — same
    // inline-SVG-in-JS approach as badgeIcon(), just a different trio.
    reviewSummaryIcon(kind) {
      const icons = {
        room: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11.5L12 4l8 7.5"/><path d="M6 10v9h12v-9"/></svg>',
        use: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
        configuration: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 20V10M12 20V4M18 20v-7"/></svg>'
      };
      return icons[kind] || '';
    }

    // Jumps back to Customize with the row the visitor clicked Change on
    // already expanded — same picker panel as Customize itself, just
    // reachable from Review too instead of only "go back a whole screen".
    changeFromReview(index) {
      this.state.expandedRow = index;
      this.renderRecommendedSummary();
      this.renderComponentList();
      this.updateRunningTotal();
      this.goTo('customize');
    }

    renderFinalReview() {
      const rec = this.state.recommended;
      const score = this.computeScore();
      const wrap = this.querySelector('[data-review-wrap]');
      const roomLabel = (this.state.room || '').replace(' room', '');
      const isMusic = this.state.category === 'music';
      const items = rec.components.slice();
      if (this.state.addonSelected && rec.addon) items.push(rec.addon);
      const rows = items.map((c, i) => {
        // Only a real, indexed component (not the addon, appended above
        // without an index of its own) can be swapped from here.
        const canChange = i < rec.components.length;
        return `
        <div class="byos-review__row">
          <img src="${this.imageFor(c)}" alt="${c.title}">
          <div class="byos-review__row-info">
            <span class="byos-slot__role">${this.slotLabel(c.slot, c)}</span>
            <span class="byos-slot__title">${c.title}${c.qty > 1 ? ' &times; ' + c.qty : ''}</span>
          </div>
          <span class="byos-slot__price">${this.formatMoney(c.price * c.qty)}</span>
          ${canChange ? `<button type="button" class="btn btn--secondary byos-review__row-change" data-review-change="${i}">Change</button>` : ''}
        </div>
      `;
      }).join('');

      const scoreBar = (label, value) => `
        <div class="byos-score">
          <span class="byos-score__label">${label}</span>
          <div class="byos-score__track"><div class="byos-score__fill" style="width:${value * 10}%"></div></div>
          <span class="byos-score__value">${value}/10</span>
        </div>
      `;

      wrap.innerHTML = `
        <div class="byos-review__summary">
          <span class="byos-review__pill"><span class="byos-review__pill-icon">${this.reviewSummaryIcon('room')}</span>${roomLabel}</span>
          <span class="byos-review__pill"><span class="byos-review__pill-icon">${this.reviewSummaryIcon('use')}</span>${this.state.category}</span>
          <span class="byos-review__pill"><span class="byos-review__pill-icon">${this.reviewSummaryIcon('configuration')}</span>${this.state.systemType}</span>
        </div>
        <div class="byos-review">${rows}</div>
        <div class="byos-scores">
          ${isMusic ? '' : scoreBar('Movies', score.movies)}
          ${scoreBar('Music', score.music)}
          ${scoreBar('Bass', score.bass)}
          ${scoreBar('Room Fit', score.roomFit)}
          ${scoreBar('Overall', score.overall)}
        </div>
      `;

      wrap.querySelectorAll('[data-review-change]').forEach((btn) => {
        btn.addEventListener('click', () => this.changeFromReview(Number(btn.dataset.reviewChange)));
      });
      this.updateRunningTotal();
    }

    // ---------- checkout ----------

    bindCheckout() {
      const addBtn = this.querySelector('[data-add-to-cart]');
      if (addBtn) addBtn.addEventListener('click', () => this.addSystemToCart(addBtn));
      const installBtn = this.querySelector('[data-install-cta]');
      if (installBtn) installBtn.addEventListener('click', () => this.openInstallWhatsapp());
    }

    // "Have Cinematics Install It" opens WhatsApp with the system pre-filled
    // so the team gets the real recommendation, not just a generic enquiry.
    openInstallWhatsapp() {
      const rec = this.state.recommended;
      if (!rec || !this.whatsappNumber) return;
      const roomLabel = (this.state.room || '').replace(' room', '');
      const addonLine = (this.state.addonSelected && rec.addon)
        ? [`- ${this.slotLabel(rec.addon.slot, rec.addon)}: ${rec.addon.title}`] : [];
      const lines = [
        "Hi, I'd like Cinematics to build and install this system:",
        `${rec.brand} ${this.state.systemType} System (${this.state.category}, ${roomLabel} room)`,
        ...rec.components.map((c) => `- ${this.slotLabel(c.slot, c)}: ${c.title}${c.qty > 1 ? ' x' + c.qty : ''}`),
        ...addonLine,
        `Total: ${this.formatMoney(rec.total)}`
      ];
      const url = `https://wa.me/${this.whatsappNumber}?text=${encodeURIComponent(lines.join('\n'))}`;
      window.open(url, '_blank');
    }

    addSystemToCart(button) {
      const rec = this.state.recommended;
      if (!rec) return;
      const items = rec.components.map((c) => ({ id: c.variant_id, quantity: c.qty || 1 }));
      if (this.state.addonSelected && rec.addon) items.push({ id: rec.addon.variant_id, quantity: rec.addon.qty || 1 });

      const cartDrawer = document.querySelector('cart-drawer');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      const originalText = button.textContent;
      button.textContent = 'Adding…';

      const body = { items: items };
      if (cartDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      fetch(this.cartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            button.textContent = 'Something went wrong — try again';
            return;
          }
          if (cartDrawer) {
            cartDrawer.renderContents(response);
            cartDrawer.classList.add('active');
            document.querySelector('body').classList.add('no-scroll');
          } else {
            window.location = window.routes.cart_url;
          }
          button.textContent = originalText;
        })
        .catch((e) => {
          console.error(e);
          button.textContent = 'Something went wrong — try again';
        })
        .finally(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        });
    }
  });
}
